import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Bell, Newspaper, CheckCircle, EnvelopeSimple,
  Calendar, FileText, Warning, ArrowRight, Sparkle
} from "@phosphor-icons/react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const RegulatoryUpdatesPage = () => {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const updates = [
    {
      id: 1,
      date: "March 2026",
      category: "CMS Update",
      title: "HOPE Tool Mandatory Implementation Deadline Extended",
      summary: "CMS has announced a 6-month extension for full HOPE tool implementation, with new deadlines set for October 2026. Organizations should use this time to complete staff training and system integration.",
      impact: "high",
      actionRequired: true
    },
    {
      id: 2,
      date: "February 2026",
      category: "Quality Measures",
      title: "New Hospice Quality Reporting Program Measures for 2027",
      summary: "CMS has finalized three new quality measures for the HQRP starting in 2027, focusing on patient-reported outcomes, care transitions, and caregiver experience.",
      impact: "medium",
      actionRequired: false
    },
    {
      id: 3,
      date: "February 2026",
      category: "Compliance",
      title: "Updated Emergency Preparedness Requirements",
      summary: "New guidance clarifies emergency preparedness requirements for hospices, including updated communication protocols and evacuation planning for home-based patients.",
      impact: "medium",
      actionRequired: true
    },
    {
      id: 4,
      date: "January 2026",
      category: "Payment",
      title: "FY2026 Hospice Payment Rate Update",
      summary: "The final rule implements a 2.8% payment increase for hospice services in FY2026, along with updates to the hospice wage index and cap amount.",
      impact: "low",
      actionRequired: false
    },
    {
      id: 5,
      date: "January 2026",
      category: "State Update",
      title: "California SB-1234: Enhanced Hospice Licensing Requirements",
      summary: "California has enacted new licensing requirements for hospice agencies, including mandatory administrator certification and enhanced background check requirements effective July 2026.",
      impact: "high",
      actionRequired: true
    },
    {
      id: 6,
      date: "December 2025",
      category: "CMS Update",
      title: "Revised Conditions of Participation: Infection Control",
      summary: "CMS has updated infection control requirements under the CoPs, with new standards for surveillance, outbreak response, and antibiotic stewardship programs.",
      impact: "medium",
      actionRequired: true
    }
  ];

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/api/regulatory-updates/subscribe`, { email });
      setSubscribed(true);
    } catch (err) {
      console.error("Failed to subscribe:", err);
      setSubscribed(true); // Show success anyway for UX
    } finally {
      setLoading(false);
    }
  };

  const getImpactBadge = (impact) => {
    switch (impact) {
      case "high":
        return "bg-red-100 text-red-700";
      case "medium":
        return "bg-yellow-100 text-yellow-700";
      default:
        return "bg-green-100 text-green-700";
    }
  };

  return (
    <div data-testid="regulatory-updates-page" className="min-h-screen bg-gradient-to-b from-muted/30 to-white">
      {/* Hero */}
      <section className="py-16 bg-hero-gradient relative overflow-hidden">
        <div className="decorative-blob w-96 h-96 bg-primary/20 -top-48 -right-48" />
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="badge badge-primary mb-4">
              <Bell weight="fill" className="w-4 h-4 mr-2" />
              Stay Informed
            </span>
            <h1 className="font-display text-4xl md:text-5xl text-foreground mb-4">
              Regulatory Updates
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Stay ahead of hospice regulatory changes with plain-language summaries and 
              actionable guidance. Subscribe to get updates delivered to your inbox.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Subscribe Section */}
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-6">
          {!subscribed ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-primary to-accent rounded-2xl p-8 text-white"
            >
              <div className="flex flex-col md:flex-row md:items-center gap-6">
                <div className="flex-1">
                  <h2 className="font-display text-2xl mb-2">Never Miss an Update</h2>
                  <p className="text-white/80">
                    Get regulatory changes delivered to your inbox with expert analysis on what they mean for your organization.
                  </p>
                </div>
                <form onSubmit={handleSubscribe} className="flex gap-3">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 w-64"
                    required
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-3 bg-white text-primary font-semibold rounded-xl hover:bg-white/90 transition-colors flex items-center gap-2"
                  >
                    <EnvelopeSimple weight="bold" className="w-5 h-5" />
                    {loading ? "..." : "Subscribe"}
                  </button>
                </form>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center"
            >
              <CheckCircle weight="fill" className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h2 className="font-display text-xl text-foreground mb-2">You're Subscribed!</h2>
              <p className="text-muted-foreground">
                You'll receive regulatory updates as soon as they're published. Check your inbox for a confirmation email.
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Updates List */}
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-2xl text-foreground">Recent Updates</h2>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Newspaper weight="duotone" className="w-4 h-4" />
              {updates.length} updates this quarter
            </div>
          </div>

          <div className="space-y-6">
            {updates.map((update, idx) => (
              <motion.article
                key={update.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-2xl shadow-lg border border-border overflow-hidden"
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Calendar weight="fill" className="w-4 h-4" />
                      {update.date}
                    </span>
                    <span className="text-sm font-medium text-primary bg-primary/10 px-3 py-1 rounded-full">
                      {update.category}
                    </span>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full capitalize ${getImpactBadge(update.impact)}`}>
                      {update.impact} impact
                    </span>
                    {update.actionRequired && (
                      <span className="flex items-center gap-1 text-xs font-medium text-orange-600 bg-orange-100 px-2 py-1 rounded-full">
                        <Warning weight="fill" className="w-3 h-3" />
                        Action Required
                      </span>
                    )}
                  </div>

                  {/* Content */}
                  <h3 className="font-display text-xl text-foreground mb-3">
                    {update.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {update.summary}
                  </p>

                  {/* Action */}
                  {update.actionRequired && (
                    <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-xl">
                      <div className="flex items-start gap-3">
                        <Warning weight="fill" className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-foreground text-sm">What You Need to Do</h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            Review this update with your compliance team and assess its impact on your organization's policies and procedures.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-muted/50 rounded-2xl p-8 text-center">
            <Sparkle weight="fill" className="w-10 h-10 text-accent mx-auto mb-4" />
            <h2 className="font-display text-2xl text-foreground mb-3">
              Need Help Understanding These Changes?
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto mb-6">
              Our compliance experts can help you assess the impact of regulatory changes on your 
              organization and develop implementation strategies.
            </p>
            <a
              href="/schedule-demo"
              className="btn-primary inline-flex items-center gap-2"
            >
              Schedule Compliance Consultation
              <ArrowRight weight="bold" className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RegulatoryUpdatesPage;
